import {
  type APIError,
  type NotificationId,
  isDefined,
  type NotificationReadInput,
  type NotificationReadOutput,
  type NotificationArchiveInput,
  type NotificationArchiveOutput,
} from '@terros/common'
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query'
import { mutationUpdate, type UpdateHookResponse } from '../hooks'
import { useServerRequest } from '../api'
import { coordinateNotificationItemUpdate, optimisticallyRead, type OptimisticChange } from './optimistic'

export function useMarkNotificationRead(): UpdateHookResponse<NotificationReadInput, NotificationReadOutput> {
  const request = useServerRequest<NotificationReadInput, NotificationReadOutput>('/notification/read')
  const client = useQueryClient()
  const mutation = useMutation<NotificationReadOutput, APIError, NotificationReadInput, OptimisticChange>({
    mutationKey: ['notification-center', 'read-update'],
    mutationFn: request,
    onMutate: (input) => optimisticallyRead(client, input),
    onError: (_error, _input, change) => change?.rollback(),
    onSettled: (_data, _error, _input, change) => change?.finish(),
  })
  return mutationUpdate(mutation, mutation.mutateAsync)
}

export function useArchiveNotification(): UpdateHookResponse<NotificationArchiveInput, NotificationArchiveOutput> {
  const request = useServerRequest<NotificationArchiveInput, NotificationArchiveOutput>('/notification/archive')
  const client = useQueryClient()
  const mutation = useMutation<NotificationArchiveOutput, APIError, NotificationArchiveInput, OptimisticChange>({
    mutationKey: ['notification-center', 'archive-update'],
    mutationFn: request,
    onMutate: (input) => coordinateNotificationItemUpdate(client, input.ids),
    onSettled: (_data, _error, _input, change) => change?.finish(),
  })
  return mutationUpdate(mutation, mutation.mutateAsync)
}

export function useNotificationItemUpdating(id: NotificationId): boolean {
  return (
    useIsMutating({
      mutationKey: ['notification-center'],
      predicate: (mutation) => {
        const input = mutation.state.variables
        return (
          typeof input === 'object' &&
          isDefined(input) &&
          'ids' in input &&
          Array.isArray(input.ids) &&
          input.ids.includes(id)
        )
      },
    }) > 0
  )
}
